import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  UserX, RefreshCw, StopCircle, PowerOff, Power, X, Activity, ListChecks,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading, Pager } from '../components/gtpl/GtplKit';

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

// Suspend.aspx requires picking one of GTPL's own fixed reasons — same modal shape as the
// account-level Suspend/Activate elsewhere (GtplSaathiSubscriberPage, CustomerDetailPage),
// just scoped to a bare accountNumber here since these rows have no CRM customer at all.
function SuspendModal({ row, action, reasons, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) { toast.error('Select a reason'); return; }
    setSaving(true);
    try {
      const { data } = await gtplSaathiAPI.suspendActive({ accountNumber: row.accountNumber, action, reason, notes: notes.trim() || undefined });
      if (data.queued) toast(data.message || 'Queued — will run once a fresh session is saved', { icon: '⏳' });
      else toast.success(action === 'ACTIVE' ? 'Activated on GTPL Saathi' : 'Suspended on GTPL Saathi');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Suspend/Activate failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{action === 'ACTIVE' ? 'Activate' : 'Suspend'} {row.accountNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Reason *</label>
              <select className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="">— Select —</option>
                {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className={`btn ${action === 'ACTIVE' ? 'btn-primary' : 'btn-danger'}`} disabled={saving}>
              {saving ? 'Saving…' : action === 'ACTIVE' ? 'Activate' : 'Suspend'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Opposite direction from the GTPL vs CRM page: finds accounts that are ACTIVE on
// gtplsaathi.com but have no matching Account No. in our own Customer collection at all —
// real connections GTPL is tracking that this CRM has never heard of (or lost track of).
// GTPL Saathi has no bulk "list every subscriber" API — the only bulk view is its own
// "Subscriber List with Active Package" report, which this drives with a real headless
// browser (see gtplSaathiService.scrapeActiveCatvSubscribers) across every page (~845
// accounts / ~40+ pages at last count), so a run takes a while and runs in the background —
// same run/poll shape as the GTPL vs CRM page.
export default function GtplOrphanAccountsPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canCheck = hasModuleRight('gtplSaathi', 'orphanCheck');
  const canSuspend = hasModuleRight('gtplSaathi', 'suspendActive');

  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loadingRows, setLoadingRows] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [suspendModal, setSuspendModal] = useState(null); // { row, action }
  const pollRef = useRef(null);

  useEffect(() => {
    if (!canSuspend) return;
    gtplSaathiAPI.getSuspendReasons().then(({ data }) => setReasons(data.data || [])).catch(() => {});
  }, [canSuspend]);

  const loadRows = useCallback(async (runId) => {
    if (!runId) { setRows([]); return; }
    setLoadingRows(true);
    try {
      const { data } = await gtplSaathiAPI.getOrphanCheckRows({ runId, page, limit: 50 });
      setRows(data.rows || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load rows');
    } finally {
      setLoadingRows(false);
    }
  }, [page]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getOrphanCheckLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load check status');
      return null;
    }
  }, []);

  useEffect(() => { loadLatest().then((r) => loadRows(r?._id)); }, []); // eslint-disable-line
  useEffect(() => { if (run?._id) loadRows(run._id); }, [page]); // eslint-disable-line

  // Poll while a scrape is actively running in the background.
  useEffect(() => {
    const active = run?.status === 'running';
    if (!active) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows(latest?._id);
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run?.status, loadLatest, loadRows]);

  const handleRunCheck = async () => {
    setStarting(true);
    try {
      const { data } = await gtplSaathiAPI.runOrphanCheck();
      setRun(data.data);
      setPage(1);
      setRows([]);
      toast.success('GTPL Not In CRM check started — this runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start check');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const { data } = await gtplSaathiAPI.stopOrphanCheck();
      setRun(data.data);
      toast.success('Stop requested — finishing the page currently in progress, then it\'ll halt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const isRunning = run?.status === 'running';
  const runTone = isRunning ? 'info' : run?.status === 'failed' ? 'bad' : run?.status === 'cancelled' ? 'mute' : 'ok';
  const runLabel = isRunning ? (run.cancelRequested ? 'Stopping…' : 'Running') : run?.status === 'failed' ? 'Failed' : run?.status === 'cancelled' ? 'Stopped' : 'Completed';

  return (
    <Screen>
      <PageHead icon={UserX} tone="ok" title="GTPL Not In CRM"
        sub={'Scrapes GTPL Saathi\'s own "Subscriber List with Active Package" report across every entity and cross-checks each account against the CRM. GTPL has no bulk export for this, so a full run pages through the whole report one screen at a time and can take a few minutes — it keeps running in the background even if you navigate away.'}>
        {isRunning && (
          <button type="button" className="btn btn-sm btn-danger" disabled={stopping || run?.cancelRequested || !canCheck} onClick={handleStop}>
            <StopCircle size={14} /> {run?.cancelRequested || stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={starting || isRunning || !canCheck} onClick={handleRunCheck}>
          <RefreshCw size={14} style={starting || isRunning ? { animation: 'spin 1s linear infinite' } : undefined} />
          {isRunning ? 'Scraping…' : starting ? 'Starting…' : 'Run Check'}
        </button>
      </PageHead>

      {!canCheck && <Notice tone="bad">You don't have permission to run this check.</Notice>}

      <Panel icon={Activity} tone={run ? runTone : 'mute'} title={run ? `Latest check: ${runLabel}` : 'Latest check'}
        sub={run ? `${isRunning ? 'Started' : 'Completed'} ${fmtDateTime(isRunning ? run.startedAt : run.completedAt)}` : undefined}>
        {!run ? (
          <Empty icon={UserX} title="No check has been run yet" />
        ) : (
          <>
            <Stats>
              <Stat tone="info" value={run.totalGtplActive || 0} label="GTPL active accounts scraped" />
              <Stat tone={run.missingCount ? 'bad' : 'ok'} value={run.missingCount || 0} label="Missing from CRM" />
            </Stats>
            {run.failMessage && (
              <div style={{ marginTop: 12 }}><Notice tone={run.status === 'cancelled' ? 'mute' : 'bad'}>{run.failMessage}</Notice></div>
            )}
          </>
        )}
      </Panel>

      {run && (
        <Panel icon={ListChecks} tone={pagination.total ? 'bad' : 'mute'} title="Accounts missing from the CRM" count={pagination.total}
          right={
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loadingRows} onClick={() => loadRows(run._id)}>
              <RefreshCw size={13} style={loadingRows ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
            </button>}>
          {loadingRows ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty tone={isRunning ? undefined : 'ok'}
              title={isRunning ? 'Results will appear here once the scrape finishes comparing against the CRM…' : 'None found — every active GTPL Saathi account has a matching CRM record.'} />
          ) : (
            <div className="gx-list">
              {rows.map((r) => (
                <Item key={r._id} tone="bad"
                  title={r.name || '—'} sub={<span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span>}
                  right={r.gtplStatus ? <Pill tone="mute" dot>{r.gtplStatus}</Pill> : null}
                  meta={<>
                    <span>Mobile: {r.mobile || '—'}</span>
                    <span>Entity: {r.entityCode || '—'}</span>
                  </>}
                  actions={canSuspend ? <>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setSuspendModal({ row: r, action: 'SUSPEND' })}>
                      <PowerOff size={12} /> Suspend
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setSuspendModal({ row: r, action: 'ACTIVE' })}>
                      <Power size={12} /> Active
                    </button>
                  </> : null} />
              ))}
            </div>
          )}

          <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
        </Panel>
      )}

      {suspendModal && (
        <SuspendModal row={suspendModal.row} action={suspendModal.action} reasons={reasons}
          onClose={() => setSuspendModal(null)} onDone={() => setSuspendModal(null)} />
      )}
    </Screen>
  );
}
